import {
  NEXUS_DEMO_JOURNEY_THOUGHT_DETAILS,
  buildNexusDemoJourneySummaryFinalBlocks,
} from '../demo/nexusDemoJourneySummaryContent.js'
import { formatWorkedForLabel } from '@/components/ChatResponseElements/AIResponseLog/formatWorkedForLabel.js'
import { computeStreamTypingDuration } from '../streaming/computeStreamTypingDuration.js'
import {
  randomPreamblePauseMs,
  scaleChatStreamContentDelay,
  scaleChatStreamDelay,
} from '../streaming/chatStreamSpeed.js'
import { runTypewriter } from '../streaming/runTypewriter.js'
import { runWorkflowStepsInStream } from './runWorkflowStepsInStream.js'
import { updatePendingNexusDemoJourneySummaryMessage } from './patchNexusDemoJourneySummaryMessage.js'
import { ensureNexusJourneyArtifactsRegistered } from './nexusDemoJourneySummaryStreamLoopImpl.js'

export function startNexusDemoJourneySummaryStreamLoop(ctx) {
  const { tid, pendingId, signal, session, setMessagesByThreadId } = ctx
  const streamStartedAt = Date.now()

  ensureNexusJourneyArtifactsRegistered()

  const state = {
    thoughtDetails: /** @type {string[]} */ ([]),
    blocks: /** @type {object[]} */ ([]),
  }

  const workedSummary = () => formatWorkedForLabel(Date.now() - streamStartedAt)

  const patch = (patchFields) => {
    updatePendingNexusDemoJourneySummaryMessage(setMessagesByThreadId, tid, pendingId, patchFields)
  }

  const schedule = (ms, fn) => {
    session.addTimeout(() => {
      if (signal.aborted) {
        session.dispose()
        return
      }
      fn()
    }, ms)
  }

  const runAfterContent = (ms, fn) => schedule(scaleChatStreamContentDelay(ms), fn)

  const runTypewriterThought = (text, onUpdate, onDone) => {
    runTypewriter({
      session,
      signal,
      text,
      durationMs: scaleChatStreamDelay(
        computeStreamTypingDuration(text.length, {
          min: 1000,
          max: 2000,
        }),
      ),
      onUpdate,
      onDone,
    })
  }

  const runTypewriterContent = (text, onUpdate, onDone) => {
    runTypewriter({
      session,
      signal,
      text,
      durationMs: scaleChatStreamContentDelay(computeStreamTypingDuration(text.length)),
      onUpdate,
      onDone,
    })
  }

  const runPreambleThinking = (onDone) => {
    patch({
      demoStreamActive: true,
      thoughtSummary: 'Thinking…',
      thoughtDetails: [],
      blocks: [],
      askBarAgentId: 'software-engineer',
      askBarPersonaId: 'architect',
      agentBrand: 'SIA',
      agentName: 'Master Root Agent',
    })
    schedule(randomPreamblePauseMs(), onDone)
  }

  const runThoughtParagraphs = (onDone) => {
    let paraIdx = 0

    const runNextParagraph = () => {
      if (paraIdx >= NEXUS_DEMO_JOURNEY_THOUGHT_DETAILS.length) {
        patch({ thoughtSummary: workedSummary(), thoughtDetails: [...state.thoughtDetails] })
        schedule(randomPreamblePauseMs(), onDone)
        return
      }

      const fullText = NEXUS_DEMO_JOURNEY_THOUGHT_DETAILS[paraIdx]
      state.thoughtDetails.push('')
      const currentParaIndex = state.thoughtDetails.length - 1
      patch({ thoughtDetails: [...state.thoughtDetails] })

      runTypewriterThought(
        fullText,
        (partial) => {
          state.thoughtDetails[currentParaIndex] = partial
          patch({ thoughtDetails: [...state.thoughtDetails] })
        },
        () => {
          paraIdx++
          schedule(randomPreamblePauseMs(), runNextParagraph)
        },
      )
    }

    runNextParagraph()
  }

  const runHeadingType = (level, content, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'heading', level, content: '' })
    patch({ blocks: [...state.blocks] })
    runTypewriterContent(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'heading', level, content: partial }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  const runMarkdownType = (content, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'markdown', content: '' })
    patch({ blocks: [...state.blocks] })
    runTypewriterContent(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'markdown', content: partial }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  const runWorkflowSteps = (templates, onDone) => {
    const logIndex = state.blocks.length
    state.blocks.push({ type: 'aiResponseLog', status: 'ok', showLogStatus: false, steps: [] })
    patch({ blocks: [...state.blocks] })

    const patchLog = (steps) => {
      state.blocks[logIndex] = {
        type: 'aiResponseLog',
        status: 'ok',
        showLogStatus: false,
        steps,
      }
      patch({ blocks: [...state.blocks] })
    }

    runWorkflowStepsInStream({
      templates,
      session,
      signal,
      runAfter: schedule,
      onStepsChange: patchLog,
      onDone,
    })
  }

  const runTableReveal = (block, onDone) => {
    const blockIndex = state.blocks.length
    const rows = block.data ?? []
    state.blocks.push({ type: 'table', data: [] })
    patch({ blocks: [...state.blocks] })

    let rowIdx = 0

    const addNextRow = () => {
      if (rowIdx >= rows.length) {
        onDone()
        return
      }
      state.blocks[blockIndex].data.push(rows[rowIdx])
      patch({ blocks: [...state.blocks] })
      rowIdx++
      runAfterContent(100, addNextRow)
    }

    addNextRow()
  }

  const runBlocks = (blocks, onDone) => {
    let i = 0

    const addNext = () => {
      if (i >= blocks.length) {
        patch({
          demoStreamActive: false,
          thoughtSummary: workedSummary(),
          nexusJourneySummaryCompleted: true,
          askBarAgentId: 'software-engineer',
          askBarPersonaId: 'architect',
          blocks: [...state.blocks],
        })
        session.dispose()
        onDone?.()
        return
      }

      const block = blocks[i]

      if (block.type === 'markdown' && typeof block.content === 'string') {
        runMarkdownType(block.content, () => {
          i++
          runAfterContent(150, addNext)
        })
        return
      }

      if (block.type === 'heading' && typeof block.content === 'string') {
        runHeadingType(block.level, block.content, () => {
          i++
          runAfterContent(150, addNext)
        })
        return
      }

      if (block.type === 'aiResponseLog' && Array.isArray(block.stepTemplates)) {
        runWorkflowSteps(block.stepTemplates, () => {
          i++
          runAfterContent(150, addNext)
        })
        return
      }

      if (block.type === 'table' && Array.isArray(block.data)) {
        runTableReveal(block, () => {
          i++
          runAfterContent(200, addNext)
        })
        return
      }

      state.blocks.push(block)
      patch({ blocks: [...state.blocks] })
      i++
      runAfterContent(180, addNext)
    }

    addNext()
  }

  runPreambleThinking(() => {
    runThoughtParagraphs(() => {
      runBlocks(buildNexusDemoJourneySummaryFinalBlocks({ streaming: true }))
    })
  })
}
